import * as React from "react";

import {
  IBasePickerProps,
  IBasePickerSuggestionsProps,
  KeyCodes,
  MessageBar,
  MessageBarType,
  setComponentRef,
  Spinner,
  SpinnerSize
} from "@bps/fluent-ui";

import {
  ExtendedPickerProps,
  PickerQueryResult
} from "./ExtendedPicker.types.ts";
import { IExtendedBasePicker } from "./IExtendedBasePicker.ts";

type InnerState<TItem> = {
  isInputEmpty: boolean;
  disableEmptySuggestions?: boolean;
  isLoadingMissingItems?: boolean;
  error?: string;
  lastSearchResult?: PickerQueryResult<TItem> | undefined;
  canSearch?: boolean;
};

export type IHasKey = {
  key: string;
};

/**
 * Extends a picker by separating the type of searched items from the suggestion items (IPersonaProps, ITag, ...)
 * It also modified the type of onChange and selectedItems to work with item keys.
 *
 * resolveDelay defaults to 500.
 * @param Component
 */
export const extendPicker = <TSuggestionItem, TItem = TSuggestionItem>(
  Component: React.FunctionComponent<IBasePickerProps<TSuggestionItem>>
): React.ComponentType<ExtendedPickerProps<TSuggestionItem, TItem>> => {
  type InnerProps = ExtendedPickerProps<TSuggestionItem, TItem>;

  class ExtendedPickerComponent extends React.Component<
    InnerProps,
    InnerState<TItem>
  > {
    private pickerInstance: IExtendedBasePicker<TSuggestionItem> | null = null;
    private unmounted = false;
    private itemsMap = new Map<string, TItem>();

    static defaultProps: Partial<InnerProps> = {
      pickerSuggestionsProps: {
        searchForMoreText: "Show more"
      },
      resolveDelay: 500,
      ignoreWhitespaceOnly: true,
      excludeSelectedItems: true
    };

    componentWillUnmount() {
      this.unmounted = true;
    }

    async componentDidMount() {
      await this._loadAnyMissingItem();
    }

    componentDidUpdate(prevProps: InnerProps) {
      const { selectedKeys: selectedItems } = this.props;
      if (
        prevProps.selectedKeys !== selectedItems &&
        JSON.stringify(prevProps.selectedKeys) !== JSON.stringify(selectedItems)
      ) {
        this._loadAnyMissingItem();
      }
    }

    private _cacheItems = (items: TItem[]) => {
      for (const item of items) {
        this.itemsMap.set(this.props.keyAccessor(item), item);
      }
    };

    private async _loadAnyMissingItem() {
      if (this.props.selectedKeys) {
        const missingItemsKeys = this.props.selectedKeys.filter(
          key => !this.itemsMap.get(key)
        );

        if (missingItemsKeys.length) {
          this.setState({ isLoadingMissingItems: true });

          try {
            const items = await this.props.onFetchItems(missingItemsKeys);
            this._cacheItems(items);
          } catch (e: any) {
            this.setState({ error: e.message, isLoadingMissingItems: false });
          }

          if (!this.unmounted) {
            this.setState({ isLoadingMissingItems: false });
          }
        }
      }
    }

    static readonly displayName = `${
      Component.displayName || (Component as any).name
    }`;

    constructor(props: InnerProps) {
      super(props);
      this.state = {
        isInputEmpty: true,
        disableEmptySuggestions: !props.showEmptySuggestionsOnFocus
      };
    }

    private _getSelectedSuggestionItems = (): TSuggestionItem[] | undefined => {
      if (!this.props.selectedKeys) {
        return undefined;
      }

      if (this.state.isLoadingMissingItems) {
        return [
          { key: this.props.selectedKeys[0] } as unknown as TSuggestionItem
        ];
      }

      return this.props.selectedKeys
        .map(key => ({ key, item: this.itemsMap.get(key) }))
        .filter(x => x.item)
        .map(({ key, item }) => ({
          ...this.props.onResolveSuggestionItem(item!, { role: "selection" }),
          key
        }));
    };

    private _onChange: IBasePickerProps<TSuggestionItem>["onChange"] =
      suggestionItems => {
        const isItemRemoved =
          (suggestionItems?.length ?? 0) <
          (this.props.selectedKeys?.length ?? 0);

        if (this.props.onChange) {
          const keys = suggestionItems!.map(
            x => (x as unknown as IHasKey).key as string
          );

          const items = keys.map(key => this.itemsMap.get(key)!);
          this.props.onChange(
            suggestionItems!.map(
              x => (x as unknown as IHasKey).key as string
            ) ?? [],
            items
          );
        }

        if (isItemRemoved) {
          this.pickerInstance?.input.current?.clear();
        }
      };

    private filterItems = (
      items: TItem[],
      selectedItemKeys: string[] | undefined
    ) => {
      // return items if there are no selected items, otherwise exclude selected items
      const filteredSelectedItems = !selectedItemKeys?.length
        ? items
        : items.filter(
            x => !(selectedItemKeys ?? []).includes(this.props.keyAccessor(x))
          );

      // just return filteredSelectedItems if excludeSelectedItems is true, and no excludeItemsFilter required
      if (this.props.excludeSelectedItems && !this.props.excludeItemsFilter) {
        return filteredSelectedItems;
      }

      // call excludeItemsFilter and pass items, and selectedItems if don't need to excluded selected items
      if (!this.props.excludeSelectedItems && this.props.excludeItemsFilter) {
        return this.props.excludeItemsFilter(items, selectedItemKeys ?? []);
      }

      // call excludeItemsFilter and pass filtered items if we need to excludeItemsFilter with excluded selected items
      if (this.props.excludeSelectedItems && this.props.excludeItemsFilter) {
        return this.props.excludeItemsFilter(
          filteredSelectedItems,
          selectedItemKeys ?? []
        );
      }

      return items;
    };

    private _resolveSuggestionItemsFromItems = (
      items: TItem[]
    ): TSuggestionItem[] => {
      return (
        this.props.excludeSelectedItems || this.props.excludeItemsFilter
          ? this.filterItems(items, this.props.selectedKeys)
          : items
      ).map(item => ({
        ...this.props.onResolveSuggestionItem(item, { role: "suggestion" }),
        key: this.props.keyAccessor(item)
      }));
    };

    private _onResolveSuggestions: IBasePickerProps<TSuggestionItem>["onResolveSuggestions"] =
      async filter => {
        this.setState({ lastSearchResult: undefined });

        if (this.props.ignoreWhitespaceOnly && filter.trim() === "") {
          return [];
        }

        const minimumCharactersToSearch =
          this.props.minimumCharactersToSearch &&
          typeof this.props.minimumCharactersToSearch === "function"
            ? this.props.minimumCharactersToSearch(filter)
            : this.props.minimumCharactersToSearch;

        if (filter?.length < (minimumCharactersToSearch ?? 0)) {
          this.setState({ canSearch: false });
          return [];
        }

        this.setState({ canSearch: true });

        const searchResult = await this.props.onSearchItems(filter);
        this._cacheItems(searchResult.results);
        const suggestionItems = this._resolveSuggestionItemsFromItems(
          searchResult.results
        );

        if (!this.unmounted) {
          this.setState({ lastSearchResult: searchResult });
        }

        return suggestionItems;
      };

    private _onGetMoreResults: IBasePickerProps<TSuggestionItem>["onGetMoreResults"] =
      async filter => {
        const previousSearchResult = this.state.lastSearchResult;
        this.setState({ lastSearchResult: undefined });
        const searchResult = await this.props.onSearchItems(
          filter,
          previousSearchResult
        );

        this._cacheItems(searchResult.results);

        // get more results will be called only once from the base picker with no known work around, we
        // don't need to keep track of the last results anymore.
        const mergedItems = mergeArrays(
          previousSearchResult?.results || [],
          searchResult.results,
          this.props.keyAccessor
        );

        return this._resolveSuggestionItemsFromItems(mergedItems);
      };

    private _getItemFromSuggestionItem = (suggestionItem: TSuggestionItem) => {
      const key = (suggestionItem as unknown as IHasKey | undefined)?.key!;
      return this.itemsMap.get(key)!;
    };

    private _onEmptyResolveSuggestions: IBasePickerProps<TSuggestionItem>["onEmptyResolveSuggestions"] =
      async () => {
        if (!this.props.onEmptySearchItems) {
          return [];
        }

        const items = await this.props.onEmptySearchItems();
        this._cacheItems(items);
        return this._resolveSuggestionItemsFromItems(items);
      };

    private _onPickerBlur: IBasePickerProps<TSuggestionItem>["onBlur"] = ev => {
      const { clearInputOnBlur = true } = this.props;

      if (this.props.onBlur) {
        this.props.onBlur(ev);
      }

      if (clearInputOnBlur) {
        this.pickerInstance?.input.current?.clear();
      }
    };

    // when called, it will force the picker to show empty suggestions
    private enableEmptySuggestions = () => {
      this.setState({ disableEmptySuggestions: false }, () => {
        if (!this.pickerInstance) {
          return;
        }
        // HACK: to force the picker to resolve empty suggestions, we trigger an input click
        this.pickerInstance.input?.current?.inputElement?.click();
      });
    };

    render() {
      const noSelectedItems = !this.props.selectedKeys?.length;
      const isInputEmpty = !this.pickerInstance?.input.current?.value.trim();
      const { onRenderItem, onRenderSuggestionsItem } = this.props;
      const onRenderItemExtended: IBasePickerProps<TSuggestionItem>["onRenderItem"] =
        onRenderItem
          ? props =>
              onRenderItem(props, this._getItemFromSuggestionItem(props.item))
          : undefined;

      const onRenderSuggestionItemsExtended: IBasePickerProps<TSuggestionItem>["onRenderSuggestionsItem"] =
        onRenderSuggestionsItem
          ? (suggestionItem, itemProps) =>
              onRenderSuggestionsItem(
                suggestionItem,
                itemProps,
                this._getItemFromSuggestionItem(suggestionItem)
              )
          : undefined;

      let noResultsFoundText: string | undefined;
      if (!isInputEmpty) {
        if (
          this.props.pickerSuggestionsProps?.noResultsFoundText !== undefined
        ) {
          noResultsFoundText =
            this.props.pickerSuggestionsProps.noResultsFoundText;
        } else if (this.state.canSearch) {
          noResultsFoundText = "No matches found";
        }
      }

      const pickerSuggestionsProps: IBasePickerSuggestionsProps<TSuggestionItem> =
        {
          ...this.props.pickerSuggestionsProps,
          searchForMoreText:
            this.state.lastSearchResult && hasMore(this.state.lastSearchResult)
              ? this.props.pickerSuggestionsProps?.searchForMoreText
              : undefined,
          noResultsFoundText
        };

      const { onItemSelected, ...props } = this.props;

      const onRenderItemPicker = this.state.isLoadingMissingItems
        ? () => <Spinner key="loading" size={SpinnerSize.xSmall} />
        : onRenderItemExtended;

      const onItemSelectedBasePicker: IBasePickerProps<TSuggestionItem>["onItemSelected"] =
        suggestionItem => {
          this.setState({ error: undefined });
          const key = (suggestionItem as unknown as IHasKey | undefined)?.key;
          const item = key ? this.itemsMap.get(key) : undefined;

          // don't select item when it is already selected
          if (key && this.props.selectedKeys?.includes(key)) {
            return null;
          }

          return onItemSelected
            ? onItemSelected(suggestionItem, item)
            : suggestionItem || null;
        };

      return (
        <>
          <Component
            {...props}
            componentRef={ref => {
              // ⚠️ HACK
              // we need access to the input element but TagPicker doesn't let us.
              // we transtype here because BasePicker holds the input ref in its input prop.
              // this can 💣 whenever the BasePicker changes its internal structure.
              const extendedPicker =
                ref as IExtendedBasePicker<TSuggestionItem> | null;
              this.pickerInstance = extendedPicker;

              setComponentRef(this.props.componentRef, extendedPicker);
            }}
            inputProps={{
              autoComplete: "off",
              ...this.props.inputProps,
              placeholder:
                noSelectedItems || this.props.alwaysShowPlaceholder
                  ? this.props.inputProps?.placeholder
                  : undefined,
              onFocus: event => {
                if (
                  !this.props.showEmptySuggestionsOnFocus &&
                  !this.state.disableEmptySuggestions
                ) {
                  // don't show empty suggestions when focused by default
                  this.setState({ disableEmptySuggestions: true });
                }

                if (this.props.inputProps?.onFocus) {
                  this.props.inputProps.onFocus(event);
                }
              },
              onClick: event => {
                if (this.state.disableEmptySuggestions) {
                  // allow empty suggestion on Click
                  this.enableEmptySuggestions();
                }

                if (this.props.inputProps?.onClick) {
                  this.props.inputProps.onClick(event);
                }
              },
              onBlur: event => {
                if (
                  !this.props.showEmptySuggestionsOnFocus &&
                  !this.state.disableEmptySuggestions
                ) {
                  this.setState({ disableEmptySuggestions: true });
                }

                if (this.props.inputProps?.onBlur) {
                  this.props.inputProps.onBlur(event);
                }
              },
              onKeyDown: event => {
                if (
                  event.keyCode === KeyCodes.enter &&
                  this.state.isInputEmpty &&
                  this.pickerInstance
                ) {
                  if (!this.pickerInstance.state.suggestionsVisible) {
                    // Enter is pressed with empty input and no visible suggestion, allow empty suggestions (if any)
                    this.enableEmptySuggestions();
                  } else {
                    // Enter is pressed with empty input and visible suggstions
                    // HACK: change the internal state of the BasePicker to hide suggestions.
                    this.pickerInstance.setState({
                      suggestionsVisible: false
                    });
                  }
                } else if (
                  this.state.disableEmptySuggestions &&
                  this.state.isInputEmpty &&
                  !this.pickerInstance?.state.suggestionsVisible
                ) {
                  switch (event.keyCode) {
                    case KeyCodes.tab:
                    case KeyCodes.escape: {
                      break;
                    }
                    default: {
                      // any key other than tab or escaope is pressed, allow empty suggestions
                      this.enableEmptySuggestions();
                    }
                  }
                }

                if (this.props.inputProps?.onKeyDown) {
                  this.props.inputProps.onKeyDown(event);
                }
              }
            }}
            selectedItems={this._getSelectedSuggestionItems()}
            onResolveSuggestions={this._onResolveSuggestions}
            onChange={this._onChange}
            onEmptyResolveSuggestions={
              this.state.disableEmptySuggestions
                ? undefined
                : this._onEmptyResolveSuggestions
            }
            pickerSuggestionsProps={pickerSuggestionsProps}
            onBlur={this._onPickerBlur}
            onGetMoreResults={this._onGetMoreResults}
            onItemSelected={onItemSelectedBasePicker}
            onRenderItem={onRenderItemPicker}
            onRenderSuggestionsItem={onRenderSuggestionItemsExtended}
          />
          {this.state.error && (
            <MessageBar messageBarType={MessageBarType.error}>
              {this.state.error}
            </MessageBar>
          )}
        </>
      );
    }
  }

  return ExtendedPickerComponent;
};

function mergeArrays<ITem extends any>(
  source: ITem[],
  arrayToMerge: ITem[],
  keyAccessor: (item: ITem) => string
) {
  const result = [...source];
  arrayToMerge.forEach(item => {
    const index = source.findIndex(
      sourceItem => keyAccessor(item) === keyAccessor(sourceItem)
    );
    if (index === -1) {
      result.push(item);
    }
  });
  return result;
}

function hasMore({ total, results, skip, take }: PickerQueryResult<any>) {
  if (total === undefined || skip === undefined || take === undefined) {
    return false;
  }
  return !!results.length && skip + take < total;
}
